import * as RD from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-50 bg-ink/40 data-[state=open]:animate-in" />
        <RD.Content
          className={clsx(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-lg border border-line bg-surface shadow-md',
            'md:inset-auto md:top-1/2 md:left-1/2 md:max-h-[86dvh] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg',
            width === 'sm' && 'md:w-[420px]',
            width === 'md' && 'md:w-[560px]',
            width === 'lg' && 'md:w-[760px]',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <RD.Title className="text-[17px] leading-6 font-semibold text-ink">{title}</RD.Title>
              {description ? (
                <RD.Description className="mt-1 text-[13px] text-ink-2">{description}</RD.Description>
              ) : (
                <RD.Description className="sr-only">{title}</RD.Description>
              )}
            </div>
            <RD.Close
              className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-tile hover:text-ink md:m-0 md:size-8"
              aria-label="Закрыть"
            >
              <X className="size-5" />
            </RD.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5">
              {footer}
            </div>
          ) : null}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

/** Боковая панель справа: история изменений, детали записи. На телефоне — на весь экран. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-50 bg-ink/30" />
        <RD.Content className="fixed inset-0 z-50 flex w-full flex-col border-line bg-surface shadow-md md:inset-y-0 md:right-0 md:left-auto md:max-w-[520px] md:border-l">
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <RD.Title className="text-[17px] leading-6 font-semibold text-ink">{title}</RD.Title>
              {description ? (
                <RD.Description className="mt-1 text-[13px] text-ink-2">{description}</RD.Description>
              ) : (
                <RD.Description className="sr-only">{title}</RD.Description>
              )}
            </div>
            <RD.Close
              className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-tile hover:text-ink md:m-0 md:size-8"
              aria-label="Закрыть"
            >
              <X className="size-5" />
            </RD.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
